// src/features/analytics/components/AnalyticsCoverageNotice.tsx

import { Alert, Anchor } from '@mantine/core';
import { IconInfoCircle } from '@tabler/icons-react';
import { useNavigate } from '@tanstack/react-router';
import { useLegacyReportsEligibility } from '@/features/legacy-reports/useLegacyReportsEligibility';
import type { DayZone, UtcRange } from '@/shared/utils/utcRange';
import { AnalyticsUtils } from '../AnalyticsUtils';
import type { AnalyticsCoverage, AnalyticsFreshness } from '../types';

interface AnalyticsCoverageNoticeProps {
  coverage?: AnalyticsCoverage;
  freshness?: AnalyticsFreshness;
  /**
   * The span the customer believes they are looking at for THIS dataset —
   * pages pass the SELECTED range (unfloored, so a preset silently clamped to
   * the product window still names what the customer picked); the heatmap
   * passes its 90-day-capped query range (its cap has its own caption and is
   * not a Legacy Reports matter). The banner names it so the text visibly
   * tracks the range control even when coverage is unchanged.
   */
  range: UtcRange;
  /** Shown when the range has no coverage at all. */
  emptyMessage?: string;
  /** The calendar-day zone the range speaks (dashboard ADR-009); UTC by default. */
  zone?: DayZone;
}

/**
 * The shared empty/partial strip (plan §"Empty, partial, stale, and
 * provisional states", customer posture revised 2026-07-23). Absence of data
 * is never rendered as a measured zero: callers place this above their
 * content and suppress zero-filled bodies when the empty state is active.
 * The only partial-coverage case a customer is told about is history missing
 * at the range START (blue informational banner + Legacy Reports pointer for
 * eligible accounts — AnalyticsUtils.coverageNoticeView). Trailing gaps and
 * account quietness are deliberately silent: zeros are the data, and both a
 * warning banner and an "Updated N days ago" badge misread a quiet account
 * as a system problem (freshness.asOf is the last DATA day, not a pipeline
 * watermark).
 */
export function AnalyticsCoverageNotice({
  coverage,
  freshness,
  range,
  emptyMessage = 'No analytics data for this range.',
  zone = 'utc',
}: AnalyticsCoverageNoticeProps) {
  const navigate = useNavigate();
  const { eligible: legacyReportsEligible } = useLegacyReportsEligibility();

  // `!coverage` restated for the type narrowing below; the predicate is the shared rule.
  if (!coverage || AnalyticsUtils.coverageIsEmpty(coverage, freshness, range)) {
    return (
      <Alert color="gray" icon={<IconInfoCircle size={16} />}>
        {emptyMessage}
      </Alert>
    );
  }

  const view = AnalyticsUtils.coverageNoticeView(
    coverage,
    range,
    Math.floor(Date.now() / 1000),
    zone
  );
  if (!view) return null;

  return (
    <Alert color="blue" icon={<IconInfoCircle size={16} />}>
      {view.message}
      {legacyReportsEligible && (
        <>
          {' '}
          Earlier activity is in{' '}
          <Anchor
            component="button"
            type="button"
            size="sm"
            onClick={() => navigate({ to: '/legacy-reports' })}
          >
            Legacy Reports
          </Anchor>
          .
        </>
      )}
    </Alert>
  );
}
